// #817: where "which item is open" lives in the URL.
//
// The app has TWO carriers for one identity, because it has two entry points to one view:
//
//   * `#item=<id>` — the tree page's own hash. Written whenever the tree selection
//     changes, and listened for by every in-app link (RequirementLink, ActivityFeed,
//     ItemTooltip) so following one never reloads the tree.
//   * `/item/<id>` — a standalone deep link: the Dashboard "Review" CTA, the item-card
//     window's open-full-page button, and notification alerts. Alerts append their own
//     `#comment-<id>` anchor, and both consumers match it with an ANCHORED regex
//     (`/^#comment-(.+)$/` in CommentsTab and BreakdownTab), so the item id cannot be
//     moved into the hash without breaking #360.
//
// Before #817 the path form rendered ItemDetails with no tree behind it at all. Prev/Next
// reads the tree's visible order, so on that route the order was empty and both buttons
// were dead — for exactly the person who arrived by a shared link, which is where "what's
// next?" is most likely to be asked. The route now renders the tree page, which already
// knows how to reveal a deep-linked item (useTreeState.openToItem, #655): it selects the
// item, loads and expands every ancestor, and scrolls the row into view.
//
// These helpers are pure and take a location-shaped object so they can be tested without
// a Router, a jsdom navigation, or a mounted tree.

export const ITEM_PATH_RE = /^\/item\/([^/?#]+)\/?$/;
export const URL_PARAM_SELECTED_ITEM = 'item';

export function itemIdFromPath(pathname) {
  const m = ITEM_PATH_RE.exec(pathname || '');
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    // A malformed escape is not a reason to lose the deep link; the raw segment is
    // still the id for every id this app generates.
    return m[1];
  }
}

export function itemIdFromHash(hash) {
  const h = (hash || '').replace(/^#/, '');
  if (!h) return null;
  return new URLSearchParams(h).get(URL_PARAM_SELECTED_ITEM);
}

// The hash WINS when both are present. The path says where you arrived; the hash says
// where in-app navigation has taken you since, and an in-app link sets the hash without
// touching the path.
export function selectedItemFromUrl(location) {
  const fromHash = itemIdFromHash(location && location.hash);
  return fromHash !== null && fromHash !== undefined
    ? fromHash
    : itemIdFromPath(location && location.pathname);
}

// Where the URL should point once `itemId` is the selection. Returns the pathname and
// hash separately so the caller can decide between history.replaceState (a path change)
// and assigning location.hash (which fires hashchange, the in-app navigation signal).
//
// On `/item/<id>` the PATH carries the identity, so the hash stays empty and any
// `#comment-<id>` anchor already there is left alone by the caller.
export function nextItemUrl(location, itemId) {
  const pathname = (location && location.pathname) || '/';
  const onItemPath = itemIdFromPath(pathname) !== null;
  // NO SELECTION KEEPS THE PATH. An earlier version returned `/` here, on the reasoning
  // that `/item/<id>` names something no longer open — and it broke #360. The tree's
  // selection is empty for the first render after ANY mount, including the one that
  // arrives on the deep link, so the effect that mirrors the selection into the URL fired
  // first and rewrote `/item/<id>#comment-<cid>` to `/` before the deep-link effect ever
  // read it. The notification landed on the dashboard with nothing selected. Caught by
  // zz_verify_notifications; the URL assertion one line above it still passed, because the
  // rewrite happened after that and the test's own screenshot is what showed it.
  //
  // Keeping the path costs only a stale URL after a manual deselect, which is what the
  // hash form does too.
  if (!itemId) return { pathname, hash: '' };
  // ON THE ITEM PATH, the path itself already names the arrival. Writing nothing keeps a
  // `#comment-<id>` anchor intact for #360, and — more importantly — keeps the URL out of
  // `history.replaceState`, which react-router does not observe: a manual path rewrite
  // leaves `useLocation()` pointing at the OLD item, and the route-item effect in
  // useTreeState then pulls the selection back to it. Measured: a mobile swipe advanced to
  // child 3 and snapped back, three attempts running.
  //
  // Once the selection MOVES OFF the arrival item, the hash carries it — the same
  // mechanism every in-app link already uses, and `selectedItemFromUrl` prefers it.
  if (onItemPath) {
    return itemIdFromPath(pathname) === itemId
      ? { pathname, hash: '' }
      : { pathname, hash: `#${URL_PARAM_SELECTED_ITEM}=${itemId}` };
  }
  return { pathname, hash: `#${URL_PARAM_SELECTED_ITEM}=${itemId}` };
}
